
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { DashboardStats, ApiServiceStats, UserSession, TokenUsageStats } from '@/types/dashboard';

export function useDashboardStats() {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [apiStats, setApiStats] = useState<ApiServiceStats[]>([]);
  const [activeSessions, setActiveSessions] = useState<UserSession[]>([]);
  const [recentTokenUsage, setRecentTokenUsage] = useState<TokenUsageStats[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchDashboardStats = async () => {
    try {
      const { data, error } = await supabase.rpc('get_dashboard_stats');
      if (error) throw error;
      setStats(data?.[0] || null);
    } catch (err: any) {
      console.error('Error fetching dashboard stats:', err);
      setError(err.message);
    }
  };

  const fetchApiStats = async () => {
    try {
      const { data, error } = await supabase.rpc('get_api_stats_by_service');
      if (error) throw error;
      setApiStats(data || []);
    } catch (err: any) {
      console.error('Error fetching API stats:', err);
      setError(err.message);
    }
  };

  const fetchActiveSessions = async () => {
    try {
      const { data, error } = await supabase
        .from('user_sessions')
        .select('*')
        .eq('is_active', true)
        .order('session_start', { ascending: false })
        .limit(20);
      
      if (error) throw error;
      setActiveSessions(data || []);
    } catch (err: any) {
      console.error('Error fetching active sessions:', err);
      setError(err.message);
    }
  };

  const fetchRecentTokenUsage = async () => {
    try {
      const { data, error } = await supabase
        .from('token_usage')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(100);
      
      if (error) throw error;
      setRecentTokenUsage(data || []);
    } catch (err: any) {
      console.error('Error fetching token usage:', err);
      setError(err.message);
    }
  };

  const fetchAllData = async () => {
    setLoading(true);
    await Promise.all([
      fetchDashboardStats(),
      fetchApiStats(),
      fetchActiveSessions(),
      fetchRecentTokenUsage(),
    ]);
    setLoading(false);
  };

  useEffect(() => {
    fetchAllData();

    // Set up realtime subscriptions
    const statsChannel = supabase
      .channel('dashboard-updates')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'user_sessions' }, () => {
        fetchActiveSessions();
        fetchDashboardStats();
      })
      .on('postgres_changes', { event: '*', schema: 'public', table: 'api_call_logs' }, () => {
        fetchApiStats();
        fetchDashboardStats();
      })
      .on('postgres_changes', { event: '*', schema: 'public', table: 'token_usage' }, () => {
        fetchRecentTokenUsage();
        fetchDashboardStats();
      })
      .on('postgres_changes', { event: '*', schema: 'public', table: 'chat_statistics' }, () => {
        fetchDashboardStats();
      })
      .subscribe();

    return () => {
      supabase.removeChannel(statsChannel);
    };
  }, []);

  return {
    stats,
    apiStats,
    activeSessions,
    recentTokenUsage,
    loading,
    error,
    refetch: fetchAllData,
  };
}
